import { CommonModule } from '@angular/common';
import { Component, ViewChild, signal, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { StockCoefficient } from '../../../models/stockCoefficient';
import { ConfirmationDialogService } from '../../../services/confirmation-dialog.service';
import { LookupTableComponent, ColumnConfig } from '../../shared/lookup-table/lookup-table.component';
import { LookupTableSearchBoxComponent } from '../../shared/lookup-table-search-box/lookup-table-search-box.component';
import { LookupTableSearchFilter } from '../../../models/lookupTableSearchFilter';
import { LutService } from '../../../services/lut.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { HotToastService } from '@ngxpert/hot-toast';
import { Helpers } from '../../../utilities/helpers';

@Component({
  selector: 'app-stock-coefficient-list',
  imports: [
    CommonModule,
    MatIconModule,
    MatButtonModule,
    LookupTableComponent,
    LookupTableSearchBoxComponent,
    NgxSpinnerModule
  ],
  templateUrl: './stock-coefficient-list.component.html',
  styleUrl: './stock-coefficient-list.component.scss'
})
export class StockCoefficientListComponent implements OnInit {
  @ViewChild(LookupTableComponent) lookupTable!: LookupTableComponent;

  stockCoefficients = signal<StockCoefficient[]>([]);
  allStockCoefficients: StockCoefficient[] = [];
  rightItem: StockCoefficient | undefined = undefined;
  matchedItems: StockCoefficient[] | undefined = undefined;
  isSearchVisible = false;
  searchFilter: LookupTableSearchFilter = {};

  // Define custom columns to show value instead of name
  columns: ColumnConfig[] = [
    { key: 'value', header: 'Value', type: 'text', sortable: true, editable: true },
    { key: 'priority', header: 'Priority', type: 'boolean', sortable: true, editable: true }
  ];

  constructor(
    private confirmationDialogService: ConfirmationDialogService,
    private lutService: LutService,
    private spinnerService: NgxSpinnerService,
    private toast: HotToastService
  ) {}

  ngOnInit(): void {
    this.loadStockCoefficients();
  }

  loadStockCoefficients(): void {
    this.spinnerService.show();
    this.lutService.getStockCoefficients().subscribe({
      next: (data) => {
        // Sort by value before setting
        const sortedData = this.sortStockCoefficients(data);
        this.allStockCoefficients = sortedData;
        this.stockCoefficients.set(sortedData);
        this.spinnerService.hide();
      },
      error: (error) => {
        console.error('Error loading stock coefficients:', error);
        this.spinnerService.hide();
      }
    });
  }

  private sortStockCoefficients(stockCoefficients: StockCoefficient[]): StockCoefficient[] {
    return [...stockCoefficients].sort((a, b) => a.value - b.value);
  }

  onCreateStockCoefficient(): void {
    this.lookupTable.startAdd();
  }

  toggleSearch(): void {
    this.isSearchVisible = !this.isSearchVisible;
  }

  getAppliedFiltersCount(): number {
    return Object.values(this.searchFilter).filter(value => value !== undefined && value !== null && value !== '').length;
  }

  onItemSelected(stockCoefficient: StockCoefficient): void {
  }

  onEditStockCoefficient(stockCoefficient: StockCoefficient): void {
  }

  onDeleteStockCoefficient(stockCoefficient: StockCoefficient): void {
    // Use value for display since name is null
    const displayName = `${stockCoefficient.value}`;
    this.confirmationDialogService.openDeleteConfirmationDialog(displayName)
      .subscribe(result => {
        if (result) {
          // Store original lists for rollback in case of error
          const originalStockCoefficients = this.stockCoefficients();
          const originalAllStockCoefficients = this.allStockCoefficients;

          // 1. Optimistically remove from local state immediately
          this.stockCoefficients.update(stockCoefficients => stockCoefficients.filter(sc => sc.id !== stockCoefficient.id));
          this.allStockCoefficients = this.allStockCoefficients.filter(sc => sc.id !== stockCoefficient.id);

          // 2. Call backend API
          this.spinnerService.show();
          this.lutService.deleteStockCoefficient(stockCoefficient.id).subscribe({
            next: () => {
              this.spinnerService.hide();
              this.toast.success('Stock coefficient deleted successfully');
              // 3. On success: Already removed from UI, nothing more to do
            },
            error: (error) => {
              console.error('Error deleting stock coefficient:', error);
              this.spinnerService.hide();
              // 4. On error: Restore original lists
              this.stockCoefficients.set(originalStockCoefficients);
              this.allStockCoefficients = originalAllStockCoefficients;
              this.toast.error(`<strong>Failed to delete Stock Coefficient:</strong></br></br> ${Helpers.parseApiError(error)}`);
            }
          });
        }
      });
  }

  onItemSave(event: {item: StockCoefficient, newName: string} | {item: StockCoefficient, newValue: string, columnKey: string}): void {
    // Store original item for rollback in case of error
    const originalItem = { ...event.item };
    const originalAllStockCoefficients = this.allStockCoefficients;

    // Prepare updated item
    let updatedItem: StockCoefficient;
    if ('newName' in event) {
      // Legacy format - simple name update
      updatedItem = { ...event.item, name: event.newName };
    } else {
      // New format - update specific column
      // Convert string to number for value field
      const newValue = event.columnKey === 'value' ? parseFloat(event.newValue) : event.newValue;
      updatedItem = { ...event.item, [event.columnKey]: newValue };
    }

    // 1. Optimistically update local state immediately and re-sort
    this.stockCoefficients.update(stockCoefficients => {
      const updated = stockCoefficients.map(sc => sc.id === event.item.id ? updatedItem : sc);
      return this.sortStockCoefficients(updated);
    });
    this.allStockCoefficients = this.sortStockCoefficients(
      this.allStockCoefficients.map(sc => sc.id === event.item.id ? updatedItem : sc)
    );

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.updateStockCoefficient(event.item.id, updatedItem).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Stock coefficient updated successfully');
        // 3. On success: Update with server response and re-sort to maintain order
        this.stockCoefficients.update(stockCoefficients => {
          const updated = stockCoefficients.map(sc => sc.id === event.item.id ? response : sc);
          return this.sortStockCoefficients(updated);
        });
        this.allStockCoefficients = this.sortStockCoefficients(
          this.allStockCoefficients.map(sc => sc.id === event.item.id ? response : sc)
        );
      },
      error: (error) => {
        console.error('Error updating stock coefficient:', error);
        this.spinnerService.hide();
        // 4. On error: Rollback to original state
        this.stockCoefficients.update(stockCoefficients =>
          stockCoefficients.map(sc => sc.id === event.item.id ? originalItem : sc)
        );
        this.allStockCoefficients = originalAllStockCoefficients;
        this.toast.error(`<strong>Failed to update Stock Coefficient:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onItemAdd(event: {[key: string]: any}): void {
    // Generate temporary ID for optimistic update
    const tempId = crypto.randomUUID();
    const valueAsNumber = parseFloat(event['value']);
    const priorityValue = event['priority'] ?? false;
    const newStockCoefficient: StockCoefficient = { id: tempId, name: '', value: valueAsNumber, priority: priorityValue, systemReserved: false };

    // 1. Optimistically add to local state immediately and sort
    this.stockCoefficients.update(stockCoefficients => this.sortStockCoefficients([...stockCoefficients, newStockCoefficient]));
    this.allStockCoefficients = this.sortStockCoefficients([...this.allStockCoefficients, newStockCoefficient]);

    // 2. Call backend API
    this.spinnerService.show();
    this.lutService.createStockCoefficient({ name: '', value: valueAsNumber, priority: priorityValue, systemReserved: false }).subscribe({
      next: (response) => {
        this.spinnerService.hide();
        this.toast.success('Stock coefficient created successfully');
        // 3. On success: Replace temp item with server response and re-sort to maintain order
        this.stockCoefficients.update(stockCoefficients => {
          const updated = stockCoefficients.map(sc => sc.id === tempId ? response : sc);
          return this.sortStockCoefficients(updated);
        });
        this.allStockCoefficients = this.sortStockCoefficients(
          this.allStockCoefficients.map(sc => sc.id === tempId ? response : sc)
        );
      },
      error: (error) => {
        console.error('Error creating stock coefficient:', error);
        this.spinnerService.hide();
        // 4. On error: Remove the temp item
        this.stockCoefficients.update(stockCoefficients =>
          stockCoefficients.filter(sc => sc.id !== tempId)
        );
        this.allStockCoefficients = this.allStockCoefficients.filter(sc => sc.id !== tempId);
        this.toast.error(`<strong>Failed to create Stock Coefficient:</strong></br></br> ${Helpers.parseApiError(error)}`);
      }
    });
  }

  onReadyToMatch(event: {rightItem?: StockCoefficient, matchedItems?: StockCoefficient[]}): void {
    this.rightItem = event.rightItem;
    this.matchedItems = event.matchedItems;
  }

  onStockCoefficientCorrected(): void {
    // Validate that we have the required data
    if (!this.rightItem || !this.matchedItems || this.matchedItems.length === 0) {
      console.error('Missing required data for merge operation');
      this.toast.error('Please select items to merge');
      return;
    }

    // Prepare the bulk merge request
    const bulkMergeRequest = {
      leftIds: this.matchedItems.map(item => item.id),
      rightId: this.rightItem.id
    };

    // Show confirmation dialog - use value for display since name is null
    const itemsToMerge = this.matchedItems.map(item => item.value.toString()).join(', ');
    this.confirmationDialogService.openConfirmationDialog(
      'Confirm Merge',
      `Are you sure you want to merge "${itemsToMerge}" into "${this.rightItem.value}"? This action cannot be undone.`,
      'Merge',
      'Cancel',
      'merge_type',
      'clear'
    ).subscribe(result => {
      if (result && this.rightItem) {
        // Store original list for rollback in case of error
        const originalStockCoefficients = this.stockCoefficients();

        // 1. Optimistically remove merged items from local state
        this.stockCoefficients.update(stockCoefficients =>
          stockCoefficients.filter(sc => !bulkMergeRequest.leftIds.includes(sc.id))
        );

        // Clear the selection state
        this.rightItem = undefined;
        this.matchedItems = undefined;

        // 2. Call backend API
        this.spinnerService.show();
        this.lutService.mergeStockCoefficientsBulk(bulkMergeRequest).subscribe({
          next: () => {
            this.spinnerService.hide();
            this.toast.success('Stock coefficients merged successfully');
            // 3. On success: Clear the lookup table's internal matching state and reload the list
            this.lookupTable.clearMatchingState();
            this.loadStockCoefficients();
          },
          error: (error) => {
            console.error('Error merging stock coefficients:', error);
            this.spinnerService.hide();
            // 4. On error: Restore original list
            this.stockCoefficients.set(originalStockCoefficients);
            // Restore selection state for retry
            this.rightItem = originalStockCoefficients.find(sc => sc.id === bulkMergeRequest.rightId);
            this.matchedItems = originalStockCoefficients.filter(sc => bulkMergeRequest.leftIds.includes(sc.id));
            this.toast.error(`<strong>Failed to merge Stock Coefficients:</strong></br></br> ${Helpers.parseApiError(error)}`);
          }
        });
      }
    });
  }

  onSearchFilterChange(searchFilter: LookupTableSearchFilter): void {
    this.searchFilter = searchFilter;
    this.spinnerService.show();
    setTimeout(() => {
      if (searchFilter.name) {
        const filtered = this.allStockCoefficients.filter(item =>
          item.value.toString().includes(searchFilter.name ?? '')
        );
        this.stockCoefficients.set(filtered);
      } else {
        this.stockCoefficients.set(this.allStockCoefficients);
      }
      this.spinnerService.hide();
    }, 1500);
  }
}
